import type { Alert, Asset, DependencyEdge, Vessel } from '@/types';
import { criticalityRadius, statusColor } from '@/lib/color';

const EMPTY_COLLECTION: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

function statusHex(a: Asset): string {
  switch (a.operational_status) {
    case 'operational':
      return '#4caf50';
    case 'degraded':
      return '#f5a623';
    case 'offline':
      return '#e53935';
    case 'decommissioned':
      return '#4a4f54';
    case 'under_construction':
      return '#5b9bd5';
    case 'planned':
      return '#6b7279';
  }
}

function circleAroundAsset(a: Asset, radiusKm: number, segments = 40): GeoJSON.Polygon {
  const lon = a.coordinates.lon;
  const lat = a.coordinates.lat;
  const coords: number[][] = [];
  for (let i = 0; i <= segments; i += 1) {
    const angle = (i / segments) * Math.PI * 2;
    const latDelta = (Math.sin(angle) * radiusKm) / 111;
    const lonDelta =
      (Math.cos(angle) * radiusKm) /
      (111 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
    coords.push([lon + lonDelta, lat + latDelta]);
  }
  return {
    type: 'Polygon',
    coordinates: [coords],
  };
}

function protectionZone(a: Asset): {
  zoneType: string;
  rule: string;
  radiusKm: number;
  color: string;
} {
  if (a.type === 'submarine_cable' || a.tags.some((tag) => tag.includes('subsea'))) {
    return {
      zoneType: 'Subsea watch area',
      rule: 'AIS gap, anchor-drag and patrol-tasking proximity correlation',
      radiusKm: 8,
      color: '#5b9bd5',
    };
  }
  if (a.type === 'pipeline' || a.sector === 'oil_gas') {
    return {
      zoneType: 'Pipeline exclusion monitor',
      rule: 'Third-party works, vessel loitering and pressure-anomaly correlation',
      radiusKm: 5,
      color: '#d4a85c',
    };
  }
  if (a.sector === 'maritime_ports' || a.type === 'port' || a.type === 'terminal') {
    return {
      zoneType: 'Port restricted area',
      rule: 'Vessel, drone and perimeter-event correlation around critical berths',
      radiusKm: 3.5,
      color: '#8fb0cc',
    };
  }
  if (a.criticality_rating >= 5) {
    return {
      zoneType: 'Critical asset protection zone',
      rule: 'Physical perimeter, drone, access-control and nearby incident correlation',
      radiusKm: 2.5,
      color: '#e0b96f',
    };
  }
  return {
    zoneType: 'Operational safety envelope',
    rule: 'Local asset-safety envelope for proximity and dependency context',
    radiusKm: 1.5,
    color: '#7ec8a0',
  };
}

export function buildAssetGeoJSON(assets: Asset[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: assets.map((a) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [a.coordinates.lon, a.coordinates.lat] },
      properties: {
        asset_id: a.asset_id,
        name: a.name,
        shortName:
          a.name.length > 28 ? a.name.slice(0, 26) + '…' : a.name,
        country: a.country,
        type: a.type,
        sector: a.sector,
        criticality: a.criticality_rating,
        operationalStatus: a.operational_status,
        sizeBase: criticalityRadius(a.criticality_rating),
        color: statusHex(a),
        statusColor: statusColor(a.operational_status),
      },
    })),
  };
}

export function buildGeofenceGeoJSON(assets: Asset[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: assets
      .filter((a) => a.geofence || a.criticality_rating >= 4)
      .map((a) => {
        const zone = protectionZone(a);
        return {
          type: 'Feature',
          geometry: a.geofence ?? circleAroundAsset(a, zone.radiusKm),
          properties: {
            asset_id: a.asset_id,
            name: a.name,
            shortName: a.name.length > 26 ? `${a.name.slice(0, 24)}…` : a.name,
            status: a.operational_status,
            criticality: a.criticality_rating,
            zoneType: zone.zoneType,
            rule: zone.rule,
            radiusKm: zone.radiusKm,
            color: zone.color,
            source: a.geofence ? 'declared boundary' : 'derived demo zone',
          },
        };
      }),
  };
}

export function buildDependencyGeoJSON(
  edges: DependencyEdge[],
  assets: Asset[],
): GeoJSON.FeatureCollection {
  const byId = new Map(assets.map((a) => [a.asset_id, a]));
  const features: GeoJSON.Feature[] = [];
  for (const e of edges) {
    const a = byId.get(e.from_asset_id);
    const b = byId.get(e.to_asset_id);
    if (!a || !b) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [a.coordinates.lon, a.coordinates.lat],
          [b.coordinates.lon, b.coordinates.lat],
        ],
      },
      properties: {
        edge_id: e.edge_id,
        relation: e.relation,
        confidence: e.confidence,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function buildInfrastructureGeoJSON(
  edges: DependencyEdge[],
  assets: Asset[],
): GeoJSON.FeatureCollection {
  const byId = new Map(assets.map((a) => [a.asset_id, a]));
  const features: GeoJSON.Feature[] = [];
  for (const e of edges) {
    const a = byId.get(e.from_asset_id);
    const b = byId.get(e.to_asset_id);
    if (!a || !b) continue;
    const sector = a.sector === b.sector ? a.sector : `${a.sector}:${b.sector}`;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [a.coordinates.lon, a.coordinates.lat],
          [b.coordinates.lon, b.coordinates.lat],
        ],
      },
      properties: {
        edge_id: e.edge_id,
        relation: e.relation,
        sector,
        hasBackup: e.has_backup ? 'true' : 'false',
        criticality: Math.max(a.criticality_rating, b.criticality_rating),
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

const WEATHER_AREAS: Array<{
  id: string;
  name: string;
  phenomenon: string;
  validWindow: string;
  operationalImpact: string;
  confidence: number;
  countries: string[];
  severity: 'info' | 'warning' | 'critical';
  color: string;
  geometry: GeoJSON.Polygon;
}> = [
  {
    id: 'WX-NORTH-SEA-WIND-2026-05-03',
    name: 'North Sea wind curtailment risk',
    phenomenon: 'Gale-force wind band',
    validWindow: 'Next 24h',
    operationalImpact: 'Wind generation curtailment and offshore access restrictions',
    confidence: 82,
    countries: ['GB', 'NO', 'DK', 'DE', 'NL', 'BE'],
    severity: 'warning',
    color: '#f5a623',
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [-2.8, 54.0],
        [8.4, 54.1],
        [9.2, 60.6],
        [1.2, 62.4],
        [-4.2, 59.3],
        [-2.8, 54.0],
      ]],
    },
  },
  {
    id: 'WX-BALTIC-ICE-WIND-2026-05-03',
    name: 'Baltic icing and gale risk',
    phenomenon: 'Marine icing + gale corridor',
    validWindow: 'Next 18h',
    operationalImpact: 'Subsea inspection delay, port disruption, exposed overhead-line icing',
    confidence: 78,
    countries: ['EE', 'LV', 'LT', 'FI', 'SE', 'DK', 'PL'],
    severity: 'warning',
    color: '#5b9bd5',
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [15.0, 55.0],
        [28.4, 55.1],
        [30.0, 62.8],
        [22.8, 65.2],
        [16.2, 62.8],
        [15.0, 55.0],
      ]],
    },
  },
  {
    id: 'WX-BLACK-SEA-CONVECTION-2026-05-03',
    name: 'Black Sea convective disruption risk',
    phenomenon: 'Severe convective storm band',
    validWindow: 'Next 12h',
    operationalImpact: 'Port restrictions, UAV visibility degradation, transmission trip risk',
    confidence: 74,
    countries: ['UA', 'XX', 'RO', 'BG', 'TR'],
    severity: 'critical',
    color: '#e53935',
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [27.4, 43.0],
        [40.8, 42.6],
        [40.4, 46.8],
        [31.2, 47.8],
        [27.2, 45.4],
        [27.4, 43.0],
      ]],
    },
  },
];

export function buildWeatherGeoJSON(assets: Asset[]): GeoJSON.FeatureCollection {
  if (assets.length === 0) return EMPTY_COLLECTION;
  const countries = new Set(assets.map((a) => a.country));
  return {
    type: 'FeatureCollection',
    features: WEATHER_AREAS
      .filter((area) => area.countries.some((country) => countries.has(country)))
      .map((area) => ({
        type: 'Feature',
        geometry: area.geometry,
        properties: {
          weather_id: area.id,
          name: area.name,
          phenomenon: area.phenomenon,
          validWindow: area.validWindow,
          operationalImpact: area.operationalImpact,
          confidence: area.confidence,
          severity: area.severity,
          color: area.color,
          affectedAssets: assets.filter((asset) => area.countries.includes(asset.country)).length,
        },
      })),
  };
}

export function buildVesselGeoJSON(vessels: Vessel[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: vessels.map((v) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [v.lon, v.lat] },
      properties: {
        mmsi: v.mmsi,
        name: v.name,
        type: v.type,
        flag: v.flag,
        course: v.course,
        speedKnots: v.speed_knots.toFixed(1),
        aisStatus: v.ais_status,
      },
    })),
  };
}

export function buildAlertHaloGeoJSON(
  alerts: Alert[],
  assets: Asset[],
): GeoJSON.FeatureCollection {
  const byId = new Map(assets.map((a) => [a.asset_id, a]));
  const features: GeoJSON.Feature[] = [];
  for (const a of alerts) {
    if (a.severity < 4) continue;
    if (a.status === 'resolved' || a.status === 'dismissed') continue;
    for (const aid of a.asset_ids) {
      const asset = byId.get(aid);
      if (!asset) continue;
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [asset.coordinates.lon, asset.coordinates.lat],
        },
        properties: { alert_id: a.alert_id, severity: a.severity },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

export function buildScenarioRingGeoJSON(
  ids: string[],
  assets: Asset[],
): GeoJSON.FeatureCollection {
  const byId = new Map(assets.map((a) => [a.asset_id, a]));
  const features: GeoJSON.Feature[] = [];
  for (const id of ids) {
    const a = byId.get(id);
    if (!a) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [a.coordinates.lon, a.coordinates.lat] },
      properties: { asset_id: a.asset_id },
    });
  }
  return { type: 'FeatureCollection', features };
}
